import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, UserPlus, Star, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, StatusBadge, CandidateAvatar, EmptyState } from "@/components/Common";
import { DocRow } from "@/components/Docs";
import { api, formatErr, fmtDate, useFetch } from "@/lib/api";

const Screening = ({ cid }) => {
  const { data, setData } = useFetch(`/admin/screening/${cid}`, [cid]);
  const [text, setText] = useState("");
  const rate = async (r) => { try { setData((await api.put(`/admin/screening/${cid}/rating`, { rating: r })).data); } catch (e) { toast.error(formatErr(e)); } };
  const add = async () => {
    try { setData((await api.post(`/admin/screening/${cid}/notes`, { text })).data); setText(""); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div data-testid={`screening-${cid}`} className="mt-4 rounded-md border border-amber-200 bg-amber-50/50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-medium text-stone-900"><Lock className="h-3.5 w-3.5" />Private screening (team only)</p>
        <div className="flex gap-0.5" data-testid={`rating-${cid}`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} data-testid={`rate-${cid}-${n}`} onClick={() => rate(n === data?.rating ? 0 : n)} aria-label={`${n} stars`}>
              <Star className={`h-5 w-5 ${n <= (data?.rating || 0) ? "fill-amber-500 text-amber-500" : "text-stone-300"}`} />
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {(data?.notes || []).map((n) => (
          <div key={n.id} className="rounded-md bg-white px-3 py-2 text-sm"><p className="text-stone-800">{n.text}</p><p className="text-[10px] font-mono text-stone-400">{n.author} · {fmtDate(n.created_at)}</p></div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Textarea data-testid={`note-input-${cid}`} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Interview impressions, English level, red flags…" className="bg-white" />
        <Button data-testid={`note-add-${cid}`} disabled={!text.trim()} onClick={add}>Add note</Button>
      </div>
    </div>
  );
};

const AppRow = ({ c, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const toggle = async () => {
    setOpen(!open);
    if (!docs) setDocs((await api.get(`/documents/owner/candidate/${c.id}`)).data);
  };
  const decide = async (status) => {
    try { onUpdate((await api.patch(`/admin/applications/${c.id}`, { status })).data); toast.success(`Application ${status.toLowerCase()}`); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div data-testid={`application-${c.id}`} className="rounded-md border border-stone-200 bg-white">
      <div className="flex flex-wrap items-center gap-4 p-5">
        <CandidateAvatar candidate={c} className="h-11 w-11" />
        <div className="min-w-0 flex-1">
          <Link to={`/admin/candidates/${c.id}`} className="font-medium hover:underline">{c.name}</Link>
          <p className="text-xs text-stone-500">{c.title} · {c.email} · applied {fmtDate(c.created_at)}</p>
        </div>
        <StatusBadge status={c.application_status} testId={`application-status-${c.id}`} />
        {c.application_status !== "Approved" && <Button size="sm" data-testid={`approve-${c.id}`} onClick={() => decide("Approved")} className="bg-emerald-700 hover:bg-emerald-800">Approve & publish</Button>}
        {c.application_status !== "Declined" && <Button size="sm" variant="outline" data-testid={`decline-app-${c.id}`} onClick={() => decide("Declined")}>Decline</Button>}
        <Button size="icon" variant="ghost" data-testid={`expand-${c.id}`} onClick={toggle}><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></Button>
      </div>
      {open && (
        <div className="space-y-2 border-t border-stone-100 p-5">
          <p className="text-sm text-stone-600">{c.summary || "No summary."}</p>
          <p className="text-xs text-stone-500">{c.experience_years} yrs · ${c.hourly_rate}/hr · {c.location} · {(c.skills || []).join(", ")}</p>
          {(docs || []).map((d) => <DocRow key={d.id} d={d} />)}
          <Screening cid={c.id} />
        </div>
      )}
    </div>
  );
};

export default function AdminApplications() {
  const { data, setData } = useFetch("/admin/applications");
  return (
    <div>
      <PageHeader eyebrow="Online submissions" title="Candidate applications" desc="Review resumes and licenses submitted through the public apply page. Approving publishes the profile to members." />
      {data?.length === 0 && <EmptyState testId="applications-empty" icon={UserPlus} title="No applications yet" desc="Share your /apply page with candidates." />}
      <div className="space-y-3">{(data || []).map((c) => <AppRow key={c.id} c={c} onUpdate={(n) => setData((p) => p.map((x) => (x.id === n.id ? n : x)))} />)}</div>
    </div>
  );
}
